import React from "react";
import { MdEdit, MdDelete, MdFavorite, MdFavoriteBorder } from "react-icons/md";
import LinkifiedText from "./LinkifiedText";
import { Comment, PostAttachment } from "../../types/community";
import { formatDateTimeWithOrdinal } from "../../utils/dateUtils";
import { getInitials } from "../../utils/formatters";

interface CommentItemProps {
  comment: Comment;
  canManage: boolean;
  liking: boolean;
  likeError?: string;
  onEdit: (comment: Comment) => void;
  onDelete: (comment: Comment) => void;
  onToggleLike: (comment: Comment) => void;
  onOpenLikers: (comment: Comment) => void;
  onImageClick: (attachments: PostAttachment[], index: number) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  canManage,
  liking,
  likeError,
  onEdit,
  onDelete,
  onToggleLike,
  onOpenLikers,
  onImageClick,
}) => {
  const commentFullName = `${comment.author.first_name || ""} ${
    comment.author.last_name || ""
  }`.trim();
  const commentDisplayName = commentFullName || comment.author.email;
  const isCommentAuthorDeleted = !!comment.author.deleted_at;
  const likeCount = comment.likes_count ?? 0;
  const isCommentLiked = Boolean(comment.is_liked_by_auth_user);

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            {comment.author.profile_image?.full_path ? (
              <img
                src={comment.author.profile_image.full_path}
                alt={commentDisplayName}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-biafit-primary">
                <span className="text-sm font-medium text-white">
                  {getInitials(
                    comment.author.first_name,
                    comment.author.last_name
                  )}
                </span>
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            {isCommentAuthorDeleted && (
              <p className="mb-0.5 text-xs font-medium text-red-600 dark:text-red-400">
                Deleted User
              </p>
            )}
            <h4 className="text-sm font-medium text-navy-700 dark:text-white">
              {commentDisplayName}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatDateTimeWithOrdinal(comment.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {canManage && (
            <button
              onClick={() => onEdit(comment)}
              className="p-1 text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
              title="Edit Comment"
            >
              <MdEdit className="h-4 w-4" />
            </button>
          )}
          {canManage && (
            <button
              onClick={() => onDelete(comment)}
              className="p-1 text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
              title="Delete Comment"
            >
              <MdDelete className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="whitespace-pre-wrap text-sm text-gray-900 dark:text-white">
        <LinkifiedText text={comment.content} />
      </div>
      {comment.attachments && comment.attachments.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {comment.attachments.map((attachment, index) => (
            <div
              key={attachment.id}
              onClick={() => onImageClick(comment.attachments!, index)}
              className="group relative block cursor-pointer overflow-hidden rounded-md border border-gray-200 dark:border-gray-700"
            >
              <img
                src={attachment.full_path}
                alt={attachment.original_name || "Comment attachment"}
                className="h-28 w-full object-cover transition-transform duration-200 group-hover:scale-105"
              />
              <div className="bg-black/0 group-hover:bg-black/40 absolute inset-0 transition-colors"></div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onToggleLike(comment)}
            disabled={liking}
            className={`flex items-center justify-center rounded-full p-1.5 transition-colors ${
              isCommentLiked
                ? "bg-red-50 text-red-500 hover:text-red-600 dark:bg-red-900/20"
                : "bg-gray-100 text-gray-500 hover:text-red-500 dark:bg-gray-800"
            } ${liking ? "cursor-not-allowed opacity-50" : ""}`}
            aria-label={isCommentLiked ? "Unlike comment" : "Like comment"}
          >
            {isCommentLiked ? (
              <MdFavorite className="h-4 w-4" />
            ) : (
              <MdFavoriteBorder className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={() => onOpenLikers(comment)}
            className="text-xs text-gray-600 transition-colors hover:text-biafit-primary dark:text-gray-300 dark:hover:text-biafit-secondary"
            aria-label="View comment likes"
          >
            {likeCount} {likeCount === 1 ? "like" : "likes"}
          </button>
          {likeError && (
            <p className="text-xs text-red-600 dark:text-red-400">
              {likeError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommentItem;
